import { assert } from '@deqo/foundation';
import { EventSourceBase } from '@deqo/podopolis';
import { Point } from '../../../../platform/geom/Point';
import { Modifiers } from '../../../../platform/input/Modifiers';

const currentBox = {
    value: null as Transformation | null,
};

export interface TransformationInput {
    position: Point;
    modifiers: Modifiers;
}

export interface TransformationStreams {
    apply: [TransformationInput];
    commit: [string[]];
    rollback: any;
}

/**
 * Defines the interface of an object that outputs a Matrix representing a transformation made from a set of positional inputs.
 */
export abstract class Transformation extends EventSourceBase<TransformationStreams> {

    /**
     * Gets the Transformation currently being performed or null if there is no transformation being performed.
     */
    public static get current() {
        return currentBox.value;
    }

    protected ticker = 0;
    protected initial = null as TransformationInput | null;
    protected latest = null as TransformationInput | null;

    constructor() {
        super();
        this.burden(() => this.rollbackIfRequired());
    }

    public abstract get key(): string;

    public begin(initialInput: { position: Point, modifiers?: Modifiers }): void {
        this.guardDestroyed();
        assert(!currentBox.value, 'Transformation.begin: cannot begin a transformation while another is being performed?');
        currentBox.value = this;
        this.initial = { modifiers: Modifiers.none, ...initialInput };
        this.initialize();
    }

    public continue(latestInput?: Partial<TransformationInput>): void {
        this.guardDestroyed();
        const position = latestInput?.position ?? this.latest?.position ?? this.initial.position;
        const modifiers = latestInput?.modifiers ?? this.latest?.modifiers ?? this.initial.modifiers;
        this.latest = { position, modifiers };
        this.apply(this.ticker === 0);
        this.ticker++;
    }

    public complete(finalInput?: Partial<TransformationInput>): void {
        this.guardDestroyed();
        this.continue(finalInput);
        this.commit();
        currentBox.value = null;
        this.destroy();
    }

    protected rollbackIfRequired() {
        if (this === currentBox.value) {
            this.rollback();
            currentBox.value = null;
        }
    }

    protected abstract initialize(): void;
    protected abstract apply(first: boolean): void;
    protected abstract commit(): void;
    protected abstract rollback(): void;
}
